import * as React from 'react';
import axios from 'axios';
import {
  Box,
  Typography,
  TextField,
  Button,
  Paper,
  Autocomplete,
  Alert,
} from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import { useNavigate } from 'react-router-dom';

const API_URL = import.meta.env.VITE_API_URL;

interface City {
  citiesId: number;
  name: string;
}

interface Manager {
  managerId: number;
  name: string;
}

export default function ShopsNew() {
  const navigate = useNavigate();

  const [saving, setSaving] = React.useState(false);

  const [name, setName] = React.useState('');
  const [address, setAddress] = React.useState('');

  const [cities, setCities] = React.useState<City[]>([]);
  const [city, setCity] = React.useState<City | null>(null);

  const [managers, setManagers] = React.useState<Manager[]>([]);
  const [manager, setManager] = React.useState<Manager | null>(null);
  const [managerQuery, setManagerQuery] = React.useState('');

  const [workFrom, setWorkFrom] = React.useState('');
  const [workTo, setWorkTo] = React.useState('');

  const [errors, setErrors] = React.useState({
    name: '',
    address: '',
    common: '',
  });

  const fetchCities = async () => {
    try {
      const { data } = await axios.get(`${API_URL}/cities`);
      setCities(data.cities || []);
    } catch (err) {
      console.error(err);
    }
  };

  React.useEffect(() => {
    fetchCities();
  }, []);

  React.useEffect(() => {
    const timer = setTimeout(async () => {
      try {
        const { data } = await axios.get(`${API_URL}/managers`, {
          params: { q: managerQuery },
        });
        setManagers(data.data || data);
      } catch (err) {
        console.error(err);
      }
    }, 400);

    return () => clearTimeout(timer);
  }, [managerQuery]);

  const handleSave = async () => {
    const newErrors = {
      name: '',
      address: '',
      common: '',
    };

    if (!name.trim()) newErrors.name = 'Введіть назву';
    if (!address.trim()) newErrors.address = 'Введіть адресу';

    setErrors(newErrors);

    if (newErrors.name || newErrors.address) return;

    setSaving(true);

    try {
      await axios.post(`${API_URL}/shops`, {
        name,
        address,
        cityId: city?.citiesId || null,
        managerId: manager?.managerId || null,
        workTime: workFrom && workTo ? `${workFrom} - ${workTo}` : null,
      });

      navigate(-1);
    } catch (err: any) {
      setErrors((prev) => ({
        ...prev,
        common: err.response?.data?.error || 'Помилка при створенні',
      }));
    } finally {
      setSaving(false);
    }
  };

  return (
    <Box sx={{ p: 2, mx: 2 }}>
      <Button
        startIcon={<ArrowBackIcon />}
        onClick={() => navigate(-1)}
        sx={{ mb: 2 }}
      >
        Назад
      </Button>

      <Typography variant="h5" sx={{ mb: 3 }}>
        Створення нового магазину
      </Typography>

      {errors.common && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {errors.common}
        </Alert>
      )}

      <Paper sx={{ p: 2, mb: 3 }}>
        <TextField
          fullWidth
          label="Назва"
          value={name}
          onChange={(e) => {
            setName(e.target.value);
            setErrors((prev) => ({ ...prev, name: '', common: '' }));
          }}
          error={!!errors.name}
          helperText={errors.name}
          sx={{ mb: 2 }}
        />

        <TextField
          fullWidth
          label="Адреса"
          value={address}
          onChange={(e) => {
            setAddress(e.target.value);
            setErrors((prev) => ({ ...prev, address: '', common: '' }));
          }}
          error={!!errors.address}
          helperText={errors.address}
          sx={{ mb: 2 }}
        />

        <Autocomplete
          options={cities}
          getOptionLabel={(o) => o.name}
          value={city}
          onChange={(_, v) => setCity(v)}
          renderInput={(params) => (
            <TextField {...params} label="Місто" sx={{ mb: 2 }} />
          )}
        />

        <Box sx={{ display: 'flex', gap: 2, mb: 2 }}>
          <TextField
            fullWidth
            label="З"
            placeholder="08:00"
            value={workFrom}
            onChange={(e) => setWorkFrom(e.target.value)}
          />
          <TextField
            fullWidth
            label="По"
            placeholder="21:00"
            value={workTo}
            onChange={(e) => setWorkTo(e.target.value)}
          />
        </Box>

        <Autocomplete
          options={managers}
          getOptionLabel={(o) => o.name}
          value={manager}
          onChange={(_, v) => setManager(v)}
          inputValue={managerQuery}
          onInputChange={(_, v) => setManagerQuery(v)}
          renderInput={(params) => (
            <TextField {...params} label="Менеджер" />
          )}
        />
      </Paper>

      <Button
        variant="contained"
        fullWidth
        onClick={handleSave}
        disabled={saving}
      >
        {saving ? 'Створення...' : 'Створити'}
      </Button>
    </Box>
  );
}